import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Field, inputClass } from './BackgroundFields';
import {
  fetchHubupCommunitiesForPicker,
  fetchHubupTopicsForPicker,
} from '../utils/hubupWebCreator';
import { DEFAULT_COMMERCE_HUBUP } from '../utils/commerceHubup';

function ChannelBlock({ title, description, channelKey, value, onChange, communities, caps }) {
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const groupId = value?.groupId || '';

  useEffect(() => {
    if (!groupId) {
      setTopics([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingTopics(true);
    fetchHubupTopicsForPicker(groupId)
      .then((list) => {
        if (!cancelled) setTopics(list);
      })
      .finally(() => {
        if (!cancelled) setLoadingTopics(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const patch = (partial) => {
    onChange(channelKey, { ...value, ...partial });
  };

  if (!caps) return null;

  return (
    <div className="space-y-4 pt-2">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
        <input
          type="checkbox"
          checked={value?.enabled === true}
          onChange={(e) => patch({ enabled: e.target.checked })}
          className="rounded border-input shrink-0"
        />
        <span>Envoyer une notification HubUP</span>
      </label>
      {value?.enabled ? (
        <div className="grid sm:grid-cols-2 gap-3 pt-1">
          <Field label="Communauté">
            <select
              className={inputClass}
              value={groupId}
              onChange={(e) => {
                const next = e.target.value;
                patch({ groupId: next, topicId: '' });
              }}
            >
              <option value="">Choisir…</option>
              {communities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Rubrique">
            <select
              className={inputClass}
              value={value?.topicId || ''}
              disabled={!groupId || loadingTopics}
              onChange={(e) => patch({ topicId: e.target.value })}
            >
              <option value="">
                {!groupId
                  ? 'Choisissez une communauté'
                  : loadingTopics
                    ? 'Chargement…'
                    : 'Choisir une rubrique'}
              </option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </Field>
        </div>
      ) : null}
    </div>
  );
}

export default function CommerceHubupAlertsSettings({
  value,
  onChange,
  reservationCap,
  clickCollectCap,
}) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const state = value || DEFAULT_COMMERCE_HUBUP;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchHubupCommunitiesForPicker()
      .then((list) => {
        if (!cancelled) setCommunities(list);
      })
      .catch(() => {
        if (!cancelled) setError('Impossible de charger vos communautés HubUP.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChannelChange = (key, channel) => {
    onChange?.({ ...state, [key]: channel });
  };

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground flex items-center gap-2 py-8 justify-center">
        <Icon name="Loader2" size={18} className="animate-spin" />
        Chargement HubUP…
      </p>
    );
  }

  if (!communities.length) {
    return (
      <div className="p-8 text-center max-w-lg mx-auto">
        <Icon name="BellOff" size={28} className="mx-auto text-muted-foreground mb-3" />
        <p className="text-sm font-medium text-foreground">HubUP non disponible</p>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          Votre compte ou votre entreprise n&apos;a pas encore accès à une communauté HubUP.
          Les alertes automatiques ne peuvent pas être configurées.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-5">
      {error ? (
        <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
          {error}
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground leading-relaxed">
        Publie une annonce sur la rubrique choisie à chaque nouvelle demande. Utile pour
        prévenir l&apos;équipe sur le fil HubUP et en push mobile.
      </p>
      <ChannelBlock
        title="Réservations table"
        description="Dès qu'un client envoie une demande de réservation."
        channelKey="reservation"
        value={state.reservation}
        onChange={handleChannelChange}
        communities={communities}
        caps={reservationCap}
      />
      <ChannelBlock
        title="Click & collect"
        description="Dès qu'une commande est enregistrée (y compris en attente de paiement)."
        channelKey="clickAndCollect"
        value={state.clickAndCollect}
        onChange={handleChannelChange}
        communities={communities}
        caps={clickCollectCap}
      />
    </div>
  );
}
